/* Funnel telemetry for the ColorPark editor step.
 *
 * WHY THIS EXISTS
 *
 * `colorpark-customise.js` already emits a full lifecycle: preload_started,
 * preload_ready, customise_clicked, editor_opened, design_submitted,
 * editor_closed_abandoned and the failure paths. It dispatches each one as a
 * `colorpark:track` CustomEvent and pushes it to `window.dataLayer`.
 *
 * NOTHING LISTENS. There is no GTM on this store: the product page HTML contains
 * zero occurrences of googletagmanager, dataLayer, gtag( , clarity.ms or hotjar.
 * So the richest signal in the funnel has been firing into an empty room.
 *
 * The consequence is that the editor step has only ever been measured by
 * comparing two instruments that are not comparable: pixel AddToCart, which
 * fires on the Customise TAP before the editor is even revealed and is never
 * retracted, against a row count from a print queue that only ever contained
 * designs which never became orders. That produced a "45% completion rate" whose
 * true value is somewhere between 18% and 76% depending on an assumed CAPI
 * halving factor that has never been validated.
 *
 * This file replaces that guess with counting.
 *
 * WHY META CUSTOM EVENTS AND NOT AN ANALYTICS PRODUCT
 *
 * The pixel and CAPI are already installed, already consented, already read by
 * `./tools/meta-ads.sh pixel`. Sending here costs no new vendor, no new script,
 * no new consent surface and no deploy. `trackCustom` with a KA-prefixed name
 * cannot collide with a standard event, so this does NOT reintroduce the CAPI
 * deduplication problem that hand-rolled `fbq('track','AddToCart')` would.
 *
 * Session recording was explicitly rejected for this job: the editor is a
 * cross-origin iframe on h5.colorpark.cn and would record as a black box.
 *
 * THE DWELL BUCKETS ARE THE POINT
 *
 * Meta's stats endpoint returns COUNTS PER EVENT NAME. It will not give us a
 * distribution of a numeric property. So the duration is encoded into the event
 * NAME, which turns a counts-only API into a histogram.
 *
 * That histogram answers the only question that matters here:
 *   a spike in 0-5s   => people are bouncing or mis-tapping, and 45% is fine.
 *   a spike in 30-120s => people are trying and failing, and the editor is at fault.
 * Nothing else we can measure separates those two worlds.
 *
 * `elapsed_ms` on the event is `performance.now()`, i.e. time since PAGE load,
 * not since the editor opened. So it is differenced against the editor_opened
 * value rather than used raw.
 *
 * PRIVACY: identifiers and timings only. No artwork URL, no design token, no
 * cart token, no customer data. The design token is deliberately dropped even
 * though the source event carries it.
 */
(function () {
  'use strict';

  if (typeof window === 'undefined') return;

  /* Only the events that answer a question. The wrapper emits about twenty;
     forwarding all of them would make the pixel's event list unreadable and
     bury the six that matter. */
  var FORWARD = {
    colorpark_preload_ready: 'KAEditorPreloaded',
    colorpark_customise_clicked: 'KACustomiseClicked',
    colorpark_editor_opened: 'KAEditorOpened',
    colorpark_design_submitted: 'KADesignSubmitted',
    colorpark_editor_closed_abandoned: 'KAEditorAbandoned',
    colorpark_session_open_failed: 'KAEditorFailed',
    colorpark_callback_failed: 'KAEditorFailed',
  };

  /* Boundaries chosen to separate behaviours, not to be even. Under 5s cannot
     be a design attempt. Over 120s is someone who wanted it and did not get it. */
  function bucket(ms) {
    if (ms == null || !isFinite(ms) || ms < 0) return null;
    var s = ms / 1000;
    if (s < 5) return '0_5s';
    if (s < 30) return '5_30s';
    if (s < 120) return '30_120s';
    return '120s_plus';
  }

  var openedAtMs = null;

  function send(name, params) {
    /* fbq may be absent: ad blocker, consent decline, or the Facebook channel
       being disconnected. Never throw, never retry, never queue. A missing
       measurement is not worth risking the buy path for. */
    try {
      if (typeof window.fbq !== 'function') return;
      window.fbq('trackCustom', name, params || {});
    } catch (e) {
      /* swallowed on purpose */
    }
  }

  document.addEventListener('colorpark:track', function (evt) {
    try {
      var d = (evt && evt.detail) || {};
      var mapped = FORWARD[d.event];
      if (!mapped) return;

      /* Identifiers only, and only ones that are already public in the page.
         goods_id and variant_id make the data sliceable by model, which is what
         tells us whether abandonment is concentrated on particular handsets. */
      var params = {
        goods_id: d.goods_id != null ? String(d.goods_id) : undefined,
        variant_id: d.variant_id != null ? String(d.variant_id) : undefined,
      };

      if (d.event === 'colorpark_editor_opened') {
        openedAtMs = typeof d.elapsed_ms === 'number' ? d.elapsed_ms : null;
      }

      /* Dwell only on the two terminal states, and only if we saw the editor
         open in this page view. A resumed session from another page load has no
         honest start time, so it is sent without a bucket rather than with a
         wrong one. */
      var terminal =
        d.event === 'colorpark_design_submitted' ||
        d.event === 'colorpark_editor_closed_abandoned';

      if (terminal && openedAtMs != null && typeof d.elapsed_ms === 'number') {
        var b = bucket(d.elapsed_ms - openedAtMs);
        if (b) {
          params.dwell = b;
          /* The name-encoded twin. This is the one the stats API can actually
             aggregate; the `dwell` param above is for Events Manager. */
          send(mapped + '_' + b, params);
        }
      }

      send(mapped, params);
    } catch (e) {
      /* Telemetry must never break a purchase. */
    }
  });
})();
